import { useMemo } from "react";

type OnboardingStepType = "intro" | "question" | "multiple-choice" | "single-choice";

interface OnboardingOption {
  value: string;
  label?: string;
}

export type OnboardingStep = {
  type: OnboardingStepType;
  answerKey: string;
  title?: string;
  description?: string;
  primaryButton?: string;
  secondaryButton?: string;
  options?: OnboardingOption[];
};

interface QueryPresetConfig {
  answerKey: string;
  value: string | boolean | string[];
}

const QUERY_PRESETS: Record<string, QueryPresetConfig> = {
  booking: {
    answerKey: "first_win",
    value: "bookings",
  },
  catalog: {
    answerKey: "first_win",
    value: "catalog",
  },
  drop: {
    answerKey: "first_win",
    value: "drop",
  },
};

interface UseOnboardingQueryPresetsResult {
  steps: OnboardingStep[];
  presetAnswers: Record<string, string | boolean | string[]>;
}

const useOnboardingQueryPresets = (
  steps: OnboardingStep[],
  search: string
): UseOnboardingQueryPresetsResult => {
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const activePresets = useMemo(() => {
    return Object.entries(QUERY_PRESETS).reduce<QueryPresetConfig[]>(
      (acc, [param, presetConfig]) => {
        if (searchParams.get(param) === "true") {
          acc.push(presetConfig);
        }
        return acc;
      },
      []
    );
  }, [searchParams]);

  const presetAnswers = useMemo(() => {
    if (!activePresets.length) {
      return {};
    }

    return activePresets.reduce<Record<string, string | boolean | string[]>>(
      (acc, preset) => {
      acc[preset.answerKey] = preset.value;
      return acc;
    },
      {}
    );
  }, [activePresets]);

  const filteredSteps = useMemo(() => {
    if (!activePresets.length) {
      return steps;
    }

    const answerKeysToSkip = new Set(activePresets.map((preset) => preset.answerKey));

    return steps.filter((step) => !answerKeysToSkip.has(step.answerKey));
  }, [steps, activePresets]);

  return {
    steps: filteredSteps,
    presetAnswers,
  };
};

export default useOnboardingQueryPresets;

